// useUnreadCounts.js — real-time nav badge counters
//
// SHARED SINGLETON: this hook is consumed by BOTH App.js (nav badge) and
// AdminDashboard.js (alert banner). Naively each consumer would open its own
// onSnapshot listeners (2 queries x 2 mounts = 4 concurrent listeners for a
// regular admin), and every /admin mount re-billed the initial snapshots.
// Instead, a module-level store attaches the Firestore listeners ONCE per
// uid+role identity; all consumers subscribe to the store via
// useSyncExternalStore. Reference counting detaches the listeners when the
// LAST consumer unmounts, and any auth identity change (uid/isAnon/role)
// resets the store and re-attaches.
import { useCallback, useSyncExternalStore } from 'react';
import { db, collection, query, where, limit, onSnapshot } from '../firebase';

// ── Module-level singleton store ──────────────────────────────────────────────
const store = {
    key: null,        // identity string of the currently attached listeners
    refCount: 0,      // number of mounted hook consumers
    unsubs: [],       // active Firestore unsubscribe fns
    listeners: new Set(),  // React subscribers (onStoreChange callbacks)
    state: { userUnreadCount: 0, adminUnreadCount: 0 },
};

const getSnapshot = () => store.state;

const setState = (patch) => {
    const next = { ...store.state, ...patch };
    if (next.userUnreadCount === store.state.userUnreadCount &&
        next.adminUnreadCount === store.state.adminUnreadCount) return;
    store.state = next;  // new object identity only when values actually change
    store.listeners.forEach((l) => l());
};

const detach = () => {
    store.unsubs.forEach((u) => u());
    store.unsubs = [];
};

// Attach the Firestore listeners for the given auth identity (same query
// shapes and guards as the original per-component effects).
const attach = (uid, isAnon, role) => {
    const isAdmin = role === 'admin' || role === 'superAdmin';
    if (!uid || isAnon || !db) return;

    const B = collection(db, 'public/data/bookings');

    // ── User: orders where unreadByUser === true ──────────────────────────
    if (!isAdmin) {
        const q = query(B, where('userId', '==', uid), where('unreadByUser', '==', true));
        store.unsubs = [onSnapshot(q,
            (snap) => setState({ userUnreadCount: snap.size }),
            (err) => console.error('[useUnreadCounts] user:', err)
        )];
        return;
    }

    // ── Admin: orders where unreadByAdmin === true ────────────────────────
    // Per-admin isolation: a regular admin's badge counts their OWN unread orders PLUS the
    // shared unclaimed pool (disjoint sets → summed). superAdmin sees the global unread
    // count. An unscoped admin query is denied by the rules, so it MUST be scoped.
    // Cap at 100 so a large unread backlog can't turn a badge into a huge read.
    // The badge shows "99+" past this; realtime is preserved.
    const queries = (role === 'superAdmin')
        ? [query(B, where('unreadByAdmin', '==', true), limit(100))]
        : [
            query(B, where('unreadByAdmin', '==', true), where('assignedAdminId', '==', uid), limit(100)),
            query(B, where('unreadByAdmin', '==', true), where('assignedAdminId', '==', null), limit(100)),
        ];
    const counts = queries.map(() => 0);
    store.unsubs = queries.map((q, i) => onSnapshot(q,
        (snap) => { counts[i] = snap.size; setState({ adminUnreadCount: counts.reduce((a, b) => a + b, 0) }); },
        (err) => console.error('[useUnreadCounts] admin:', err)
    ));
};

// Acquire a reference for the given identity. First consumer (or an identity
// change) attaches the listeners; returns a release fn that detaches when the
// last consumer lets go.
const acquire = (uid, isAnon, role) => {
    const key = `${uid || ''}|${isAnon ? 1 : 0}|${role || ''}`;
    if (store.key !== key) {
        // Auth identity changed (or first mount) → hard reset the singleton.
        detach();
        store.key = key;
        setState({ userUnreadCount: 0, adminUnreadCount: 0 });
        attach(uid, isAnon, role);
    }
    store.refCount += 1;

    let released = false;
    return () => {
        if (released) return;
        released = true;
        store.refCount -= 1;
        if (store.refCount <= 0) {
            store.refCount = 0;
            detach();
            store.key = null;  // next acquire re-attaches (and resets counts)
        }
    };
};

/**
 * Returns live unread counts for navigation badges.
 * userUnreadCount  – bookings with admin reply the user hasn't seen
 * adminUnreadCount – bookings where admin needs to respond (new order / new user msg)
 *
 * All consumers share ONE set of Firestore listeners (see store above).
 */
export const useUnreadCounts = (user, userData) => {
    const uid = user?.uid;
    const isAnon = !!user?.isAnonymous;
    const role = userData?.role;

    const subscribe = useCallback((onStoreChange) => {
        store.listeners.add(onStoreChange);
        const release = acquire(uid, isAnon, role);
        return () => {
            store.listeners.delete(onStoreChange);
            release();
        };
    }, [uid, isAnon, role]);

    return useSyncExternalStore(subscribe, getSnapshot);
};
